import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CheckSquare, Lightbulb, Bell, LayoutDashboard, Brain, PanelsTopLeft } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../ui/tooltip";
import { cn } from "../../../lib/utils";

import ActionItems from "./actionItems";
import Insights from "./insights";
import UpcomingReminders from "./reminders";
import MmStudio from "../../mentalModel/MmStudio";
import WorkspaceBoard from "./WorkspaceBoard";
import WorkspaceFocus from "./WorkspaceFocus";
import { useDirection } from "../../../hooks/useDirection";
import { useWorkspaceStatsQuery } from "../../../hooks/usePortalData";
import { useZoneSidebar } from "../../../context/ZoneSidebarContext";

const FOCUS_SECTIONS = ["action_items", "insights", "reminders", "mental_models"] as const;
type FocusSection = (typeof FOCUS_SECTIONS)[number];

const MyWorkspace = () => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { data: statsData } = useWorkspaceStatsQuery();
  const { portalContainer, collapsed: zoneSidebarCollapsed } = useZoneSidebar();

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("workspace_active_tab") || "hub";
  });

  // Design 3b: sections open as list + detail ("focus"); the classic full-tool
  // views stay one click away and the choice is remembered.
  const [sectionView, setSectionViewState] = useState<"focus" | "classic">(() =>
    localStorage.getItem("workspace_section_view") === "classic" ? "classic" : "focus"
  );
  const setSectionView = (v: "focus" | "classic") => {
    setSectionViewState(v);
    localStorage.setItem("workspace_section_view", v);
  };
  // When the board hands off to a section it can point at a specific item
  const [focusSeed, setFocusSeed] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );

  const [counts, setCounts] = useState({
    pendingActions: 0,
    totalInsights: 0,
    upcomingReminders: 0,
    mentalModels: 0,
  });

  // Sync React Query stats into local counts state
  useEffect(() => {
    if (statsData) {
      setCounts(prev => ({ ...prev, ...statsData }));
    }
  }, [statsData]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);

    return () => {
      mediaQuery.removeEventListener("change", handler);
    };
  }, []);

  // Sync active tab from localStorage when navigating here via drawer/bottom nav
  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail === "my_workspace") {
        const tab = localStorage.getItem("workspace_active_tab") || "action_items";
        setActiveTab(tab);
      }
    };
    window.addEventListener("portal-tab-change", handler);
    return () => window.removeEventListener("portal-tab-change", handler);
  }, []);

  // Return to hub after a quick-action dialog (opened from hub) is closed
  useEffect(() => {
    const handler = () => handleWorkspaceTabChange("hub");
    window.addEventListener("workspace-return-to-hub", handler);
    return () => window.removeEventListener("workspace-return-to-hub", handler);
  }, []);

  // Direct tab switch (e.g. lift buttons jump to Mental Models)
  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail;
      if (typeof tab === "string") handleWorkspaceTabChange(tab);
    };
    window.addEventListener("workspace-set-tab", handler);
    return () => window.removeEventListener("workspace-set-tab", handler);
  }, []);

  const handleWorkspaceTabChange = (value: string) => {
    setActiveTab(value);
    localStorage.setItem("workspace_active_tab", value);
  };

  const menuItems = [
    {
      id: "action_items",
      label: t("action_items"),
      icon: CheckSquare,
      count: counts.pendingActions,
      countLabel: t("pending_items", "to complete"),
      color: "text-primary",
      bgColor: "bg-primary/5",
      hoverBg: "hover:bg-primary/5/50",
      activeBg: "bg-primary/5",
      activeBorder: "border-l-blue-500",
    },
    {
      id: "insights",
      label: t("insights"),
      icon: Lightbulb,
      count: counts.totalInsights,
      countLabel: t("workspace_insights_count_label", "captured"),
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      hoverBg: "hover:bg-amber-50/50",
      activeBg: "bg-amber-50",
      activeBorder: "border-l-amber-500",
    },
    {
      id: "reminders",
      label: t("upcoming_reminders"),
      icon: Bell,
      count: counts.upcomingReminders,
      countLabel: t("workspace_reminders_count_label", "coming up"),
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      hoverBg: "hover:bg-purple-50/50",
      activeBg: "bg-purple-50",
      activeBorder: "border-l-purple-500",
    },
    {
      id: "mental_models",
      label: t("mm_studio_title", "Mental Models"),
      icon: Brain,
      count: counts.mentalModels,
      countLabel: t("mm_workspace_count_label", "in progress"),
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      hoverBg: "hover:bg-teal-50/50",
      activeBg: "bg-teal-50",
      activeBorder: "border-l-teal-500",
    },
  ];

  // Open a section — optionally pointing the focus view at a specific item
  // (how the board's rows deep-link into their sections).
  const openSection = (section: string, itemId?: string) => {
    setFocusSeed(itemId ?? null);
    handleWorkspaceTabChange(section);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "action_items":
        return <ActionItems onCountChange={(count) => setCounts(prev => ({ ...prev, pendingActions: count }))} />;
      case "insights":
        return <Insights onCountChange={(count) => setCounts(prev => ({ ...prev, totalInsights: count }))} />;
      case "reminders":
        return <UpcomingReminders onCountChange={(count) => setCounts(prev => ({ ...prev, upcomingReminders: count }))} />;
      case "mental_models":
        return <MmStudio onCountChange={(count) => setCounts(prev => ({ ...prev, mentalModels: count }))} />;
      default:
        return null;
    }
  };

  if (isMobile) {
    // Design 6a — the board IS the mobile hub: all four sections in one
    // scroll, section pills to jump, one "+" to capture.
    if (activeTab === "hub" || !FOCUS_SECTIONS.includes(activeTab as FocusSection)) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-3 pt-3 pb-4">
            <WorkspaceBoard mobile onOpenSection={openSection} />
          </div>
        </div>
      );
    }

    // Design 6b — the section as two screens: zone chips switch sections in
    // place, tapping an item pushes the full detail (classic stays one tap away)
    if (
      sectionView === "focus" &&
      !(activeTab === "mental_models" && sessionStorage.getItem("mm_open_object"))
    ) {
      return (
        <WorkspaceFocus
          key={`${activeTab}:${focusSeed ?? ""}`}
          mobile
          section={activeTab as FocusSection}
          initialId={focusSeed}
          counts={counts}
          onSwitchSection={(section) => openSection(section)}
          onSwitchToClassic={() => setSectionView("classic")}
        />
      );
    }

    // Classic full-tool view on mobile, with the way back to focus
    return (
      <div className="flex flex-col h-full">
        <div className="flex justify-end px-3 pt-2">
          <button
            onClick={() => setSectionView("focus")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors"
          >
            <PanelsTopLeft className="w-3.5 h-3.5" />
            {t("workspace_view_focus")}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pt-2 pb-4">
          {renderContent()}
        </div>
      </div>
    );
  }

  const tooltipSide = isRTL ? "left" as const : "right" as const;

  // Desktop: Portal sidebar into ZoneSidebar, render content only
  const sidebarContent = (
    <div className={cn("p-2 space-y-1", zoneSidebarCollapsed && "px-1")}>
      {/* Hub / Overview */}
      {zoneSidebarCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleWorkspaceTabChange("hub")}
              className={cn(
                "w-full flex items-center justify-center h-9 rounded-lg transition-colors",
                activeTab === "hub"
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-gray-100 text-gray-700"
              )}
            >
              <LayoutDashboard className="w-5 h-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            {t("overview", "Overview")}
          </TooltipContent>
        </Tooltip>
      ) : (
        <button
          onClick={() => handleWorkspaceTabChange("hub")}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
            activeTab === "hub"
              ? "bg-primary/10 text-primary font-medium"
              : "hover:bg-gray-100 text-gray-700"
          )}
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="flex-1 text-start text-sm truncate">
            {t("overview", "Overview")}
          </span>
        </button>
      )}

      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;

        if (zoneSidebarCollapsed) {
          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleWorkspaceTabChange(item.id)}
                  className={cn(
                    "w-full flex items-center justify-center h-9 rounded-lg transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-gray-100 text-gray-700"
                  )}
                >
                  <Icon className={cn("w-5 h-5", item.color)} />
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide}>
                {item.label} ({item.count})
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <button
            key={item.id}
            onClick={() => handleWorkspaceTabChange(item.id)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
              isActive
                ? "bg-primary/10 text-primary font-medium"
                : "hover:bg-gray-100 text-gray-700"
            )}
          >
            <Icon className={cn("w-5 h-5", item.color)} />
            <span className="flex-1 text-start text-sm truncate">
              {item.label}
            </span>
            <span className="text-xs text-gray-500 tabular-nums">
              {item.count}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <TooltipProvider>
      {/* Portal sidebar content into ZoneSidebar */}
      {portalContainer && !isMobile && createPortal(sidebarContent, portalContainer)}

      <div className="flex flex-col h-full overflow-hidden">
        <main className="flex-1 overflow-hidden bg-white">
          {activeTab === "hub" || !FOCUS_SECTIONS.includes(activeTab as FocusSection) ? (
            /* Design 3a — the overview IS the board: all four sections at once */
            <div className="h-full p-6 overflow-y-auto">
              <WorkspaceBoard onOpenSection={openSection} />
            </div>
          ) : sectionView === "focus" &&
            /* A lift ("work on this in Mental Models") targets the full studio */
            !(activeTab === "mental_models" && sessionStorage.getItem("mm_open_object")) ? (
            /* Design 3b — list + detail; full-bleed, panes scroll on their own */
            <WorkspaceFocus
              key={`${activeTab}:${focusSeed ?? ""}`}
              section={activeTab as FocusSection}
              initialId={focusSeed}
              onSwitchToClassic={() => setSectionView("classic")}
            />
          ) : (
            <div className="h-full flex flex-col min-h-0">
              <div className="flex justify-end px-6 pt-3">
                <button
                  onClick={() => setSectionView("focus")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                >
                  <PanelsTopLeft className="w-3.5 h-3.5" />
                  {t("workspace_view_focus")}
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
                {renderContent()}
              </div>
            </div>
          )}
        </main>
      </div>
    </TooltipProvider>
  );
};

export default MyWorkspace;
